import { State } from 'redux/initialState';
import { useSelector } from 'react-redux';
import { FC, createContext, useContext, useState } from 'react';

import checkIsValidEmail from 'utils/checkIsValidEmail';
import { CustomAsyncSelectOption } from 'modules/_shared/components/CustomAsyncSelect/types/CustomAsyncSelectOption';

import { CoOwner, SelectorOption } from 'types';
import { useGetPartnerOptions } from '../hooks/useGetPartnerOptions';
import { CoOwnerViewContextType } from '../types/CoOwnerViewContextType';
import { coOwnerViewInitValue } from '../constants/coOwnerViewInitValue';
import { checkIsValidPercent } from '../utils/checkIsValidPercent';

const CoOwnerViewContext =
  createContext<CoOwnerViewContextType>(coOwnerViewInitValue);

export const CoOwnerViewProvider: FC<{
  children: React.ReactNode; // eslint-disable-line
  actualPercent: number; // eslint-disable-line
  handleAddCoOwner: (data: CoOwner) => void; // eslint-disable-line
}> = ({ children, actualPercent, handleAddCoOwner }) => {
  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );

  const partnerOptionsResult = useGetPartnerOptions({
    societyId: actualSociety?._id,
  });

  const { partnerOptions } = partnerOptionsResult.data;
  const { fetchPartnersByTerm: handleFetchPartnersByTerm } =
    partnerOptionsResult.actions;

  const [selectedPartner, setSelectedPartner] = useState<SelectorOption | null>(
    null
  );
  const [partnerId, setPartnerId] = useState<string | null>(null);
  const [name, setName] = useState<string>('');
  const [cif, setCif] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [percent, setPercent] = useState<number>(0);
  const [comments, setComments] = useState<string>('');
  const [isValidPercent, setIsValidPercent] = useState<boolean>(false);
  const [isValidEmail, setIsValidEmail] = useState<boolean>(false);

  const updateSelectedPartner = (option: CustomAsyncSelectOption | null) => {
    setSelectedPartner(option);
    setPartnerId(option?.value || null);
    setName(option?.name || '');
    setCif(option?.cif || '');
    setEmail(option?.email || '');
  };

  const handleChangeSelectedPartner = (
    option: CustomAsyncSelectOption | null
  ) => {
    updateSelectedPartner(option);
    const isValidEmail = checkIsValidEmail(option?.email || '');
    setIsValidEmail(isValidEmail);
  };

  const handleChangeName = (value: string) => {
    setName(value);
  };

  const handleChangeCif = (value: string) => {
    setCif(value);
  };

  const handleChangeEmail = (value: string) => {
    setEmail(value);

    const isValidEmail = checkIsValidEmail(value);
    setIsValidEmail(isValidEmail);
  };

  const handleChangePercent = (
    event: React.ChangeEvent<HTMLInputElement> // eslint-disable-line
  ) => {
    const value = event?.target?.value;
    const percent = Number(value);

    setPercent(percent);

    const isValidPercent = checkIsValidPercent({
      percent,
      actualPercent,
    });
    setIsValidPercent(isValidPercent);
  };

  const handleChangeComments = (value: string) => {
    setComments(value);
  };

  const handleCleanForm = () => {
    setName('');
    setCif('');
    setEmail('');
    setPercent(0);
    setComments('');
    setSelectedPartner(null);
    setPartnerId(null);
    setIsValidPercent(false);
  };

  const handleSaveCoOwner = () => {
    handleAddCoOwner({
      partner: partnerId,
      name,
      cif,
      email,
      percent,
      comment: comments,
    });
    handleCleanForm();
  };

  return (
    <CoOwnerViewContext.Provider
      value={{
        data: {
          selectedPartner,
          name,
          cif,
          email,
          percent,
          comments,
          partnerOptions,
          isValidPercent,
          isValidEmail,
        },
        actions: {
          handleChangeSelectedPartner,
          handleChangeName,
          handleChangeCif,
          handleChangeEmail,
          handleChangePercent,
          handleChangeComments,
          handleFetchPartnersByTerm,
          handleSaveCoOwner,
        },
      }}
    >
      {children}
    </CoOwnerViewContext.Provider>
  );
};

export const useCoOwnerView = () => {
  const context = useContext(CoOwnerViewContext);
  if (!context) {
    throw new Error('useCoOwnerView must be used within a CoOwnerViewProvider');
  }
  return context;
};
